import { Routes } from '@angular/router';

import { adminGuard } from '@infinitform/cabinet-features/guards/admin.guard';
import { adminOrAnalystGuard } from '@infinitform/cabinet-features/guards/admin-or-analyst.guard';
import { licenseAdminGuard } from '@infinitform/cabinet-features/guards/license-admin.guard';
import { authGuard } from '@infinitform/cabinet-features/guards/auth.guard';
import { notAuthGuard } from '@infinitform/cabinet-features/guards/notauth.guard';
import { onboardingGuard } from '@infinitform/cabinet-features/guards/onboarding.guard';
import { organizationOnboardingGuard } from '@infinitform/cabinet-features/guards/organization-onboarding.guard';
import { subscriptionGuard } from '@infinitform/cabinet-features/guards/subscription.guard';
import { maintenanceGuard } from '@infinitform/cabinet-features/guards/maintenance.guard';
import { pluginGuard } from '@infinitform/cabinet-features/guards/plugin.guard';
import { unsavedChangesGuard } from '@infinitform/shared-ui/cabinet/settings';

export const routes: Routes = [
  { path: '', redirectTo: '/user/projects', pathMatch: 'full' },
  {
    path: 'pricing',
    title: 'Pricing',
    loadComponent: () => import('@infinitform/cabinet-features/pages/public/pricing/pricing.component').then((m) => m.PricingComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'login',
    title: 'Login',
    loadComponent: () => import('@infinitform/cabinet-features/pages/authorization/login/login.component').then((m) => m.LoginComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'register',
    title: 'Register',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/authorization/register/register.component').then((m) => m.RegisterComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'activate',
    title: 'Activate Account',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/authorization/activate/activate.component').then((m) => m.ActivateComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'password-activation',
    title: 'Set Password',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/authorization/password-activation/password-activation.component').then(
        (m) => m.PasswordActivationComponent
      ),
    canActivate: [notAuthGuard]
  },
  {
    path: 'forgot-password',
    title: 'Forgot Password',
    loadComponent: () => import('@infinitform/cabinet-features/pages/authorization/forgot/forgot.component').then((m) => m.ForgotComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'reset-password',
    title: 'Reset Password',
    loadComponent: () => import('@infinitform/cabinet-features/pages/authorization/reset/reset.component').then((m) => m.ResetComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'auth/error',
    title: 'Authentication Error',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/authorization/auth-error/auth-error.component').then((m) => m.AuthErrorComponent)
  },
  {
    path: 'onboarding',
    title: 'Onboarding',
    loadComponent: () => import('@infinitform/cabinet-features/pages/user/onboarding/onboarding.component').then((m) => m.OnboardingComponent),
    canActivate: [authGuard, onboardingGuard]
  },
  {
    path: 'organization/member-setup',
    title: 'Member Setup',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/authorization/member-setup/member-setup.component').then((m) => m.MemberSetupComponent),
    canActivate: [notAuthGuard]
  },
  {
    path: 'organization/onboarding',
    title: 'Organization Setup',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/organization/onboarding/organization-onboarding.component').then(
        (m) => m.OrganizationOnboardingComponent
      ),
    canActivate: [authGuard, organizationOnboardingGuard]
  },
  {
    path: 'after-payment',
    title: 'Payment Complete',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/authorization/after-payment/after-payment.component').then((m) => m.AfterPaymentComponent)
  },
  {
    path: 'maintenance',
    title: 'Maintenance',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/maintenance/maintenance.component').then((m) => m.MaintenancePageComponent),
  },
  {
    path: 'offline-activation',
    title: 'Offline License Activation',
    loadComponent: () =>
      import('@infinitform/cabinet-features/pages/public/offline-activation/offline-activation.component').then(
        (m) => m.OfflineActivationComponent
      ),
  },
  {
    path: '',
    loadComponent: () => import('./components/layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [authGuard, maintenanceGuard, subscriptionGuard],
    children: [
      {
        path: 'user/profile',
        title: 'Profile',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/user-profile/user-profile.component').then((m) => m.UserProfileComponent),
        canActivate: [authGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'user/settings',
        title: 'Settings',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/user-settings/user-settings.component').then((m) => m.UserSettingsComponent),
        canActivate: [authGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'user/settings/2fa',
        title: 'Two-Factor Authentication',
        loadComponent: () =>
          import(
            '@infinitform/cabinet-features/pages/authorization/two-factor-auth/two-factor-management/two-factor-management.component'
          ).then((m) => m.TwoFactorManagementComponent),
        canActivate: [authGuard]
      },
      {
        path: 'user/subscription',
        title: 'Subscription',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/user-settings/subscription-info/subscription-info.component').then(
            (m) => m.SubscriptionInfoComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'user/projects',
        title: 'Projects',
        loadComponent: () => import('@infinitform/cabinet-features/pages/user/projects/projects.component').then((m) => m.ProjectsComponent),
        canActivate: [authGuard]
      },
      {
        path: 'user/projects/open/:id',
        title: 'Project',
        loadComponent: () =>
          import('@infinitform/cabinet-features/open-projects/open-project-page/open-project-page.component').then(
            (m) => m.OpenProjectPageComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'user/categories',
        title: 'Folders',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/categories/categories.component').then((m) => m.CategoriesComponent),
        canActivate: [authGuard]
      },
      {
        path: 'user/materials',
        title: 'Materials',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/materials/materials.component').then((m) => m.MaterialsComponent),
        canActivate: [authGuard]
      },
      {
        path: 'user/plugins',
        title: 'CAD Add-ins',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/plugins/plugins.component').then((m) => m.UserPluginsComponent),
        canActivate: [authGuard, pluginGuard]
      },
      {
        path: 'user/sharing/others',
        title: 'Shared With Others',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/sharing/shared-resources/shared-resources.component').then(
            (m) => m.SharedResourcesComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'user/sharing/me',
        title: 'Shared With Me',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/sharing/shared-with-me-resources/shared-with-me-resources.component').then(
            (m) => m.SharedWithMeResourcesComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'user/help',
        title: 'Help',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/user/help/help.component').then((m) => m.HelpComponent),
        canActivate: [authGuard]
      },
      {
        path: 'company/users',
        title: 'Members',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/organization/users/users.component').then((m) => m.CompanyUsersComponent),
        canActivate: [authGuard]
      },
      {
        path: 'company/users/:id/projects',
        title: 'Member Projects',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/organization/users/user/user-projects/user-projects.component').then(
            (m) => m.CompanyUserProjectsComponent
          ),
        canActivate: [authGuard]
      },
      {
        path: 'company/settings',
        title: 'Organization Settings',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/organization/settings/settings.component').then((m) => m.CompanySettingsComponent),
        canActivate: [authGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'admin/users',
        title: 'Users',
        loadComponent: () => import('@infinitform/cabinet-features/pages/admin/users/users.component').then((m) => m.UsersComponent),
        canActivate: [adminGuard]
      },
      {
        path: 'admin/organizations',
        title: 'Organizations',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/organizations/organizations.component').then((m) => m.AdminOrganizationsComponent),
        canActivate: [adminGuard]
      },
      {
        path: 'admin/feedbacks',
        title: 'Feedbacks',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/feedbacks/feedbacks.component').then((m) => m.FeedbacksComponent),
        canActivate: [adminOrAnalystGuard]
      },
      {
        path: 'admin/users/:id/projects',
        title: 'User Projects',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/users/user/user-projects/user-projects.component').then(
            (m) => m.UserProjectsComponent
          ),
        canActivate: [adminGuard]
      },
      {
        path: 'admin/subscriptions',
        title: 'Subscriptions',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/subscriptions/subscriptions.component').then((m) => m.SubscriptionsComponent),
        canActivate: [adminGuard]
      },
      // One sidebar row, three tabbed views. The static children are declared
      // before ':id' or that param route swallows /reports and /customers.
      {
        path: 'admin/licenses',
        title: 'Licenses',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/licenses/licenses.component').then((m) => m.AdminLicensesComponent),
        canActivate: [adminGuard, licenseAdminGuard]
      },
      {
        path: 'admin/licenses/reports',
        title: 'License Reports',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/licenses/license-reports.component').then((m) => m.AdminLicenseReportsComponent),
        canActivate: [adminGuard, licenseAdminGuard]
      },
      {
        path: 'admin/licenses/customers',
        title: 'License Customers',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/licenses/license-customers.component').then((m) => m.AdminLicenseCustomersComponent),
        canActivate: [adminGuard, licenseAdminGuard]
      },
      {
        path: 'admin/licenses/:id',
        title: 'License',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/licenses/license-detail.component').then((m) => m.AdminLicenseDetailComponent),
        canActivate: [adminGuard, licenseAdminGuard]
      },
      {
        path: 'admin/statistics',
        title: 'Statistics',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/statistics/statistics.component').then((m) => m.StatisticsComponent),
        canActivate: [adminOrAnalystGuard]
      },
      {
        path: 'admin/plugins',
        title: 'CAD Add-ins',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/plugins/plugins.component').then((m) => m.PluginsComponent),
        canActivate: [adminGuard, pluginGuard]
      },
      {
        path: 'admin/settings',
        title: 'Settings',
        loadComponent: () =>
          import('@infinitform/cabinet-features/pages/admin/settings/settings.component').then((m) => m.AdminSettingsComponent),
        canActivate: [adminGuard],
        canDeactivate: [unsavedChangesGuard]
      },
      {
        path: 'about',
        title: 'About',
        loadComponent: () => import('@infinitform/cabinet-features/pages/user/about/about.component').then((m) => m.AboutComponent),
        canActivate: [authGuard]
      }
    ]
  },
  { path: '**', redirectTo: '/user/projects' }
];
